import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, isInsufficientCredits, type Book, type Job } from "../api";
import { Sparkler } from "../components/icons";

export default function Processing({
  book,
  job,
  onRetried,
}: {
  book: Book;
  job: Job | null;
  onRetried?: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryErr, setRetryErr] = useState<string | null>(null);
  const total = Math.max(1, job?.totalPages ?? 1);
  const done = Math.min(total, job?.progress ?? 0);
  const pct = job?.status === "running"
    ? Math.max(8, Math.round((done / total) * 100))
    : job?.status === "queued"
      ? 6
      : 0;
  const failed = book.status === "failed" || job?.status === "failed";

  const retry = async () => {
    setRetryBusy(true);
    setRetryErr(null);
    try {
      await api.retryBook(book.id);
      await onRetried?.();
    } catch (e) {
      setRetryErr(isInsufficientCredits(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setRetryBusy(false);
    }
  };

  return (
    <div className="tp-app">
      <div
        className="tp-screen"
        style={{
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          padding: "60px 28px", textAlign: "center",
        }}
      >
      <div style={{ position: "relative", marginBottom: 28 }}>
        <Sparkler />
      </div>
      <div className="tp-display" style={{ marginBottom: 12 }}>
        {failed ? t("processing.failedTitle") : t("processing.title")}
      </div>
      <div className="tp-body" style={{ fontSize: 16, color: "var(--tp-ink-soft)", marginBottom: 26, maxWidth: 320 }}>
        {failed ? (
          <>{t("processing.failedBody")} {job?.error ? <em>({job.error})</em> : null}</>
        ) : (
          <Trans
            i18nKey="processing.body"
            values={{ title: book.title || t("processing.defaultTitle") }}
            components={{ title: <strong style={{ color: "var(--tp-ink)" }} /> }}
          />
        )}
      </div>

      {!failed && (
        <div style={{ width: "100%", maxWidth: 320, marginBottom: 24 }}>
          <div style={{
            height: 12, background: "var(--tp-surface-alt)",
            border: "2px solid var(--tp-ink)",
            borderRadius: 999, overflow: "hidden",
          }}>
            <div style={{
              width: `${pct}%`, height: "100%",
              background: "var(--tp-accent)",
              transition: "width .24s ease",
            }} />
          </div>
          <div className="tp-eyebrow" style={{ marginTop: 8, fontSize: 10 }}>
            {job?.status === "queued"
              ? t("processing.waiting")
              : t("processing.makingPage", { current: Math.min(total, done + 1), total })}
          </div>
        </div>
      )}

      {failed && (
        <>
          {retryErr && (
            <div className="tp-error" role="alert" style={{ maxWidth: 320, marginBottom: 12 }}>{retryErr}</div>
          )}
          <button
            className="tp-btn tp-btn-primary tp-btn-block"
            onClick={retry}
            disabled={retryBusy}
            style={{ maxWidth: 320, marginBottom: 12 }}
          >
            {retryBusy ? t("processing.retrying") : t("processing.retryBook")}
          </button>
        </>
      )}

      <button className="tp-btn tp-btn-block" onClick={() => navigate("/app")} style={{ maxWidth: 320 }}>
        {failed ? t("processing.backToLibrary") : t("processing.gotItBackToLibrary")}
      </button>
      </div>
    </div>
  );
}
